import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Award, Users, Building2, Star, Quote } from "lucide-react";
import { isRTL } from "@/i18n";

const ABOUT_IMG = "https://images.pexels.com/photos/8730014/pexels-photo-8730014.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const achievementIcons = [Building2, Users, Award];
const achievementKeys = ["projects", "clients", "experience"];
const achievementValues = ["500+", "2,000+", "12+"];

export default function AboutSection() {
  const { t, i18n } = useTranslation();
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  const rtl = isRTL(i18n.language);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="about" data-testid="about-section" ref={ref} className="py-20 md:py-32 bg-[#F8F9FA]" dir={rtl ? "rtl" : "ltr"}>
      <div className="max-w-7xl mx-auto px-6 md:px-12 mb-24">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <div className={`transition-all duration-1000 ${visible ? "opacity-100 translate-x-0" : rtl ? "opacity-0 translate-x-12" : "opacity-0 -translate-x-12"}`}>
            <div className="flex items-center gap-3 mb-4">
              <div className="gold-line" />
              <span className="text-[#D4AF37] text-xs font-medium tracking-[0.25em] uppercase" style={{ fontFamily: "'Outfit', sans-serif" }}>
                {t("about.pretitle")}
              </span>
            </div>
            <h2 data-testid="about-title" className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#0A192F] tracking-tight mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
              {t("about.title_1")}{" "}
              <span className="text-[#D4AF37] italic">{t("about.title_highlight")}</span>
              {t("about.title_2") ? ` ${t("about.title_2")}` : ""}
            </h2>
            <p className="text-[#4A5568] text-base leading-relaxed mb-6" style={{ fontFamily: "'Outfit', sans-serif" }}>
              {t("about.description_1")}
            </p>
            <p className="text-[#4A5568] text-base leading-relaxed mb-8" style={{ fontFamily: "'Outfit', sans-serif" }}>
              {t("about.description_2")}
            </p>
            <div className="flex flex-wrap gap-8">
              {achievementKeys.map((key, idx) => {
                const Icon = achievementIcons[idx];
                return (
                  <div key={key} className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-[#0A192F] flex items-center justify-center">
                      <Icon className="w-5 h-5 text-[#D4AF37]" />
                    </div>
                    <div>
                      <p className="text-xl font-bold text-[#0A192F]" style={{ fontFamily: "'Playfair Display', serif" }}>{achievementValues[idx]}</p>
                      <p className="text-xs text-[#4A5568]" style={{ fontFamily: "'Outfit', sans-serif" }}>{t(`about.achievements.${key}`)}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          <div className={`relative transition-all duration-1000 delay-200 ${visible ? "opacity-100 translate-x-0" : rtl ? "opacity-0 -translate-x-12" : "opacity-0 translate-x-12"}`}>
            <div className="relative rounded-sm overflow-hidden">
              <img src={ABOUT_IMG} alt="OlyInvestment Team" className="w-full h-[400px] lg:h-[500px] object-cover" loading="lazy" />
            </div>
            <div className={`absolute -top-4 ${rtl ? "-right-4" : "-left-4"} w-24 h-24 ${rtl ? "border-t-4 border-r-4 rounded-tr-sm" : "border-t-4 border-l-4 rounded-tl-sm"} border-[#D4AF37]`} />
            <div className={`absolute -bottom-4 ${rtl ? "-left-4" : "-right-4"} w-24 h-24 ${rtl ? "border-b-4 border-l-4 rounded-bl-sm" : "border-b-4 border-r-4 rounded-br-sm"} border-[#D4AF37]`} />
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="text-center mb-14">
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="gold-line" />
            <span className="text-[#D4AF37] text-xs font-medium tracking-[0.25em] uppercase" style={{ fontFamily: "'Outfit', sans-serif" }}>
              {t("about.testimonials_pretitle")}
            </span>
            <div className="gold-line" />
          </div>
          <h2 data-testid="testimonials-title" className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#0A192F] tracking-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
            {t("about.testimonials_title")}
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {["1", "2", "3"].map((key, i) => (
            <div
              key={key}
              data-testid={`testimonial-card-${i}`}
              className={`bg-white p-8 rounded-sm border border-[#E2E8F0] hover:shadow-xl transition-all duration-500 ${
                visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
              }`}
              style={{ transitionDelay: `${0.5 + i * 0.15}s` }}
            >
              <Quote className="w-8 h-8 text-[#D4AF37]/30 mb-4" />
              <p className="text-[#4A5568] text-sm leading-relaxed mb-6 italic" style={{ fontFamily: "'Outfit', sans-serif" }}>
                "{t(`about.testimonials.${key}.text`)}"
              </p>
              <div className="flex items-center gap-1 mb-4">
                {Array.from({ length: 5 }).map((_, si) => (
                  <Star key={si} className="w-4 h-4 fill-[#D4AF37] text-[#D4AF37]" />
                ))}
              </div>
              <div>
                <p className="text-sm font-semibold text-[#0A192F]" style={{ fontFamily: "'Outfit', sans-serif" }}>{t(`about.testimonials.${key}.name`)}</p>
                <p className="text-xs text-[#4A5568]" style={{ fontFamily: "'Outfit', sans-serif" }}>{t(`about.testimonials.${key}.role`)}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
